<script lang="ts">
  import ProblemNotice from '../lib/ProblemNotice.svelte'
  import { appProblem } from '../lib/problems'
  import { t } from '../lib/i18n.svelte'
  import { dev } from '../lib/dev.svelte'
  import { onDestroy, onMount } from 'svelte'
  import Brand from '../lib/Brand.svelte'
  import Loading from '../lib/Loading.svelte'
  import Chat from '../lib/Chat.svelte'
  import Signal from '../lib/Signal.svelte'
  import { barsFor, barsLabel, type Bars } from '../lib/signal'
  import type { ChatLine } from '../lib/Chat.svelte'
  import { decode } from '../lib/social'
  import { PRESETS, prefs, savePrefs, type Preset } from '../lib/prefs.svelte'
  import { navigate } from '../lib/router.svelte'
  import { friends } from '../lib/friends.svelte'
  import { account } from '../lib/account.svelte'
  import { shell } from '../lib/shell.svelte'
  import { refreshLive } from '../lib/live.svelte'
  import WindowGrid from '../lib/WindowGrid.svelte'
  import FriendsRail from '../lib/FriendsRail.svelte'
  import SwitchWindow, { type SwitchTarget } from '../lib/SwitchWindow.svelte'
  import StartupProgress from '../lib/StartupProgress.svelte'
  import { restoreHostSession } from '../lib/host-session'
  import {
    availableShareMode,
    resolveShareMode,
    type SharePreference,
  } from '../lib/share-preference'
  import {
    activeStream,
    appInfo,
    errorMessage,
    hideMini,
    hideOverlay,
    startMarks,
    stopMarks,
    showOverlay,
    publishOverlayLine,
    onOverlaySend,
    isTauri,
    listAudioProcesses,
    listWindows,
    listMonitors,
    type MonitorInfo,
    onHostEvent,
    onStreamRecovery,
    onMiniAction,
    onMiniReady,
    onTrayAction,
    openMain,
    publishMiniState,
    sendChat,
    setBitrate,
    setLiveState,
    setTrayLabels,
    showMini,
    toggleMini,
    sharingAvailability,
    startPreview,
    startStream,
    stopStream,
    switchSource,
    type AppInfo,
    type AudioProcessInfo,
    type HostEvent,
    type HostStats,
    type MiniState,
    type ShareMode,
    type SharingAvailability,
    type StreamSummary,
    type StartupStage,
    type WindowInfo,
  } from '../lib/tauri'

  /**
   * The host panel.
   *
   * Everything here goes through the Tauri commands, so this page is also the
   * fastest way to check that the desktop side is wired up: if `list_windows`
   * fails, the failure is shown verbatim instead of being swallowed.
   */

  const inDesktopApp = isTauri()
  let { active = true }: { active?: boolean } = $props()

  let info = $state<AppInfo | null>(null)
  let windows = $state<WindowInfo[]>([])
  let monitors = $state<MonitorInfo[]>([])
  let selectedMonitor = $state<number | null>(null)
  let audioProcesses = $state<AudioProcessInfo[]>([])
  /** Set when a window is picked or switched to, so it outlives the grid that listed it. */
  let pickedWindow = $state<WindowInfo | null>(null)
  let busy = $state(false)
  let startupStage = $state<StartupStage | null>(null)
  let startupShare = $state<ShareMode>('tunnel')
  let restoring = $state(inDesktopApp)
  let restoreError = $state(false)
  let destroyed = false
  let error = $state<string | null>(null)

  // Stream settings
  let selectedHwnd = $state<number | null>(null)
  let audioPidOverride = $state<number | 'same'>('same')
  let soundMode = $state<'app' | 'system' | 'exclude' | 'none'>('app')
  /** In 'system' mode, keep Discord's audio out of the stream. */
  let skipDiscord = $state(true)
  let bitrateMbps = $state(prefs.bitrateMbps)
  let fps = $state<30 | 60>(prefs.fps)
  let viewerCap = $state(prefs.viewerCap)
  let password = $state('')
  let displayName = $state(prefs.displayName)
  let share = $state<SharePreference>(prefs.connection)
  let availability = $state<SharingAvailability | null>(null)
  let availabilityError = $state<string | null>(null)
  let checkingAvailability = $state(false)
  let availabilityTimer: ReturnType<typeof setInterval> | undefined
  const signedIn = $derived(!!account.session && !account.session.user.is_anonymous)
  const usableShare = $derived(availableShareMode(share, signedIn, availability))
  const selectedShare = $derived<ShareMode>(usableShare ?? resolveShareMode(share, signedIn))

  async function checkAvailability() {
    checkingAvailability = true
    try {
      const result = await sharingAvailability()
      if (!destroyed) {
        availability = result
        availabilityError = null
      }
      return result
    } catch (err) {
      if (!destroyed) {
        availability = null
        availabilityError = errorMessage(err)
      }
      return null
    } finally {
      if (!destroyed) checkingAvailability = false
    }
  }

  function modeUnavailable(mode: SharePreference) {
    return availableShareMode(mode, signedIn, availability) === null
  }
  let preset = $state<Preset>(prefs.preset)

  // First-run screen
  let draftName = $state(prefs.displayName)
  let draftShare = $state<SharePreference>(prefs.connection)

  // Picker and live-screen UI state
  let showSettings = $state(false)
  let showScreens = $state(false)
  let showAdv = $state(prefs.showStats)
  let copied = $state(false)
  let startedAt = $state<number | null>(null)
  let now = $state(Date.now())

  // Live stream
  let live = $state<StreamSummary | null>(null)
  let stats = $state<HostStats | null>(null)
  let warnings = $state<string[]>([])
  let switchingSource = $state(false)
  let chat = $state<ChatLine[]>([])
  /** Viewers in the room, and whether media is flowing to each. */
  let viewers = $state<Record<string, { name: string; connected: boolean }>>({})

  const BITRATE_MIN_MBPS = 2
  const BITRATE_MAX_MBPS = 20

  const selectedWindow = $derived(
    windows.find((w) => w.hwnd === selectedHwnd) ??
      (pickedWindow?.hwnd === selectedHwnd ? pickedWindow : null),
  )
  /**
   * The app to keep out of the stream, by executable name, so the host can still
   * exclude it if it starts after the stream does.
   */
  const excludeExe = $derived.by(() => {
    if (soundMode === 'system') return skipDiscord ? 'discord' : ''
    if (soundMode !== 'exclude') return ''
    if (audioPidOverride === 'same') return selectedWindow?.exe ?? ''
    return audioProcesses.find((p) => p.pid === audioPidOverride)?.exe ?? ''
  })
  const effectiveAudioPid = $derived(
    soundMode === 'none'
      ? 0
      : soundMode === 'system'
        ? 0
        : audioPidOverride === 'same'
          ? (selectedWindow?.pid ?? 0)
          : audioPidOverride,
  )
  const viewerEntries = $derived(Object.entries(viewers))
  /** Bars per viewer, from each one's own estimate against what was asked for. */
  const viewerBars = $derived.by(() => {
    const map: Record<string, Bars> = {}
    for (const stat of stats?.viewers ?? []) {
      map[stat.peer_id] = barsFor(stat.estimate_kbps, bitrateMbps * 1000)
    }
    return map
  })
  /** The viewer holding the picture back, when their connection is weak. */
  const slowViewer = $derived.by(() => {
    let worst: { name: string; bars: Bars } | null = null
    for (const [id, viewer] of Object.entries(viewers)) {
      const bars = viewerBars[id]
      if (viewer.connected && bars !== undefined && bars <= 2 && (!worst || bars < worst.bars)) {
        worst = { name: viewer.name, bars }
      }
    }
    return worst
  })
  const limitedByViewer = $derived(
    stats != null && stats.estimate_kbps != null && stats.target_kbps < bitrateMbps * 1000 * 0.95,
  )
  const elapsed = $derived.by(() => {
    const total = startedAt === null ? 0 : Math.max(0, Math.floor((now - startedAt) / 1000))
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`
  })
  const roomCode = $derived(live?.room_code ?? null)

  const onlineFriends = $derived(friends.friends.filter((f) => f.presence.state !== 'offline'))
  let invited = $state<Record<string, boolean>>({})
  // Friends told when you go live: every friend who is online, unless switched off here.
  let tellOff = $state<Record<string, boolean>>({})
  const tellList = $derived(friends.sorted.slice(0, 5))
  const toTell = $derived(onlineFriends.filter((f) => !tellOff[f.user_id]))
  const shareUrl = $derived(live?.share_url ?? '')

  function inviteFriends(summary: StreamSummary) {
    for (const friend of toTell) {
      friends.invite(friend.user_id, summary.room_code, summary.share_url)
      invited[friend.user_id] = true
    }
  }

  let unlisten: (() => void) | null = null
  let unlistenMisc: (() => void)[] = []

  const health = $derived<MiniState['health']>(
    limitedByViewer ? 'slow' : stats?.frame_rate_reduced ? 'busy' : 'great',
  )
  const miniSnapshot = $derived<MiniState>({
    live: roomCode !== null,
    elapsed,
    health,
    fps: stats ? Math.round(stats.fps) : null,
    mbps: stats ? Math.round(stats.video_kbps / 100) / 10 : null,
    encodeMs: stats ? Math.round(stats.worst_encode_ms) : null,
    viewers: viewerEntries.map(([id, v]) => ({
      id,
      name: v.name,
      connected: v.connected,
      bars: viewerBars[id] ?? 4,
      dropped: stats?.viewers.find((s) => s.peer_id === id)?.frames_dropped ?? 0,
    })),
    chatCount: chat.length,
  })

  // Keep the mini view and the tray in step with the stream.
  $effect(() => {
    if (!inDesktopApp || !roomCode) return
    void publishMiniState(miniSnapshot)
  })
  $effect(() => {
    if (!inDesktopApp || restoring || restoreError) return
    const live = roomCode !== null
    const status = live
      ? t('Live · {seats} · {time}', {
          seats: t('{n} on the sofa', { n: viewerEntries.length }),
          time: elapsed,
        })
      : t('Not sharing')
    void setLiveState({ live, status, share_url: shareUrl || null }).catch(() => {})
  })
  $effect(() => {
    if (!inDesktopApp) return
    void setTrayLabels({
      copy: t('Copy invite link'),
      mini: t('Show mini view'),
      open: t('Open Sofa'),
      stop: t('Stop sharing'),
      quit: t('Quit'),
    }).catch(() => {})
  })

  // Pictures of windows cost a capture session each, so they only exist while
  // this page is showing: leaving the route or hiding the window (tray) tears
  // them down, and nothing is captured for them until it is back. The mounted
  // session still receives events while another route is showing.
  let pageVisible = $state(
    typeof document === 'undefined' || document.visibilityState === 'visible',
  )
  let thumbs = $state<Record<number, string>>({})
  let previewUrl = $state<string | null>(null)
  let previewUnavailable = $state(false)
  let previewAttempt = $state(0)
  const pickerActive = $derived(
    inDesktopApp &&
      active &&
      !restoring &&
      !restoreError &&
      prefs.onboarded &&
      !roomCode &&
      pageVisible,
  )

  $effect(() => {
    const onVisibility = () => (pageVisible = document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  })

  // Live preview of the shared window, only while it can be seen.
  $effect(() => {
    if (!inDesktopApp || !active || !roomCode || !pageVisible) return
    if (selectedHwnd === null && selectedMonitor === null) return
    void previewAttempt
    previewUnavailable = false
    let cancelled = false
    const timeout = setTimeout(() => {
      if (!cancelled) previewUnavailable = true
    }, 5000)
    let stop: (() => Promise<void>) | null = null
    startPreview({ hwnd: selectedHwnd, monitorIndex: selectedMonitor }, (url) => {
      if (!cancelled) {
        clearTimeout(timeout)
        previewUnavailable = false
        previewUrl = url
      }
    })
      .then((stopFn) => {
        if (cancelled) void stopFn()
        else stop = stopFn
      })
      .catch(() => {
        if (!cancelled) previewUnavailable = true
      })
    return () => {
      clearTimeout(timeout)
      cancelled = true
      void stop?.()
      previewUrl = null
    }
  })

  // Tick once a second while live, for the elapsed timer.
  $effect(() => {
    if (!live) return
    const id = setInterval(() => (now = Date.now()), 1000)
    return () => clearInterval(id)
  })

  function playingSound(pid: number): boolean {
    return audioProcesses.some((p) => p.pid === pid && p.is_active)
  }

  function persistStreamPrefs() {
    prefs.preset = preset
    prefs.fps = fps
    prefs.bitrateMbps = bitrateMbps
    prefs.viewerCap = viewerCap
    prefs.connection = share
    prefs.displayName = displayName
    savePrefs()
  }

  function pickPreset(key: keyof typeof PRESETS) {
    preset = key
    fps = PRESETS[key].fps
    bitrateMbps = PRESETS[key].mbps
    persistStreamPrefs()
    void applyBitrate()
  }

  /** Moving a knob by hand leaves the named presets. */
  function customise() {
    preset = 'custom'
    persistStreamPrefs()
  }

  function finishOnboarding() {
    displayName = draftName.trim()
    share = draftShare
    prefs.onboarded = true
    persistStreamPrefs()
  }

  const presetLabel = $derived(
    preset === 'custom' ? t('Custom') : t(PRESETS[preset].label, undefined, 'preset'),
  )
  const shareLabel = $derived(
    selectedShare === 'lan'
      ? t('Same Wi-Fi')
      : selectedShare === 'server'
        ? t('Sofa server')
        : t('P2P through Cloudflare'),
  )

  async function restoreSession() {
    restoring = true
    restoreError = false
    error = null
    try {
      unlisten = await restoreHostSession(
        onHostEvent,
        activeStream,
        (snapshot) => {
          if (destroyed) return
          live = snapshot.summary
          selectedHwnd = snapshot.hwnd || null
          selectedMonitor = snapshot.monitor_index
          bitrateMbps = snapshot.summary.bitrate_bps / 1_000_000
          fps = snapshot.summary.fps as 30 | 60
          viewerCap = snapshot.summary.viewer_cap
          now = Date.now()
          startedAt = now - snapshot.elapsed_ms
          snapshot.events.forEach(handleEvent)
        },
        (event) => {
          if (!destroyed) handleEvent(event)
        },
      )
      if (destroyed) unlisten()
    } catch (err) {
      restoreError = true
      error = errorMessage(err)
    } finally {
      restoring = false
    }
  }

  onMount(async () => {
    if (!inDesktopApp) return
    void checkAvailability()
    availabilityTimer = setInterval(() => {
      if (active && !roomCode && !busy && !checkingAvailability) void checkAvailability()
    }, 30_000)
    unlistenMisc = await Promise.all([
      onStreamRecovery((event) => {
        if (destroyed) return
        if (event.state === 'recovering') {
          busy = true
          startupShare = 'tunnel'
          startupStage = 'preparing_link'
          error = null
          warnings = []
          chat = []
          resetLive()
        } else {
          busy = false
          startupStage = null
          if (event.state === 'recovered') {
            live = event.summary
            startedAt = Date.now()
            now = startedAt
            warnings = [...warnings.slice(-4), t(event.message)]
            invited = {}
            inviteFriends(event.summary)
            if (prefs.miniOnLive) void showMini().catch(() => {})
            if (prefs.chatOverlay)
              void showOverlay(prefs.chatSide, prefs.chatHotkey).catch(() => {})
            void startMarks().catch(() => {})
          } else error = event.message
        }
      }),
      onMiniReady(() => void publishMiniState(miniSnapshot)),
      onOverlaySend((text) => void hostChat(text)),
      onMiniAction((action) => {
        if (action === 'stop') void stop()
        else void openMain()
      }),
      onTrayAction((action) => {
        if (action === 'stop') void stop()
      }),
    ])
    if (destroyed) {
      unlistenMisc.forEach((off) => off())
      return
    }
    await restoreSession()
    if (destroyed) return
    try {
      info = await appInfo()
    } catch (err) {
      error = errorMessage(err)
    }
    await refreshWindows()
  })

  $effect(() => {
    shell.hosting = roomCode !== null
    // Tell the rest of the app right away instead of at its next poll.
    if (!restoring && !restoreError) void refreshLive()
  })

  onDestroy(() => {
    destroyed = true
    clearInterval(availabilityTimer)
    shell.hosting = false
    unlisten?.()
    unlistenMisc.forEach((off) => off())
  })

  function handleEvent(event: HostEvent) {
    switch (event.type) {
      case 'viewer_joined':
        viewers[event.peer_id] = { name: event.name, connected: false }
        break
      case 'viewer_connected':
        if (viewers[event.peer_id]) viewers[event.peer_id].connected = true
        break
      case 'viewer_disconnected':
        if (viewers[event.peer_id]) viewers[event.peer_id].connected = false
        break
      case 'viewer_left':
        delete viewers[event.peer_id]
        break
      case 'chat':
        chat.push(event)
        if (prefs.chatOverlay && inDesktopApp) {
          void publishOverlayLine({ name: event.name, item: decode(event.text) })
        }
        break
      case 'stats':
        stats = event
        break
      case 'warning':
        // Keep the latest few; a flapping viewer should not grow this forever.
        warnings = [...warnings.slice(-4), t(event.message)]
        break
      case 'stopped':
        if (event.reason) error = `The stream stopped: ${event.reason}`
        resetLive()
        void hideMini().catch(() => {})
        void hideOverlay().catch(() => {})
        void stopMarks().catch(() => {})
        break
    }
  }

  function resetLive() {
    live = null
    stats = null
    viewers = {}
  }

  async function applyBitrate() {
    if (!live) return
    try {
      await setBitrate(bitrateMbps * 1_000_000)
    } catch (err) {
      error = errorMessage(err)
    }
  }

  function toggleChatOverlay() {
    prefs.chatOverlay = !prefs.chatOverlay
    savePrefs()
    void (prefs.chatOverlay ? showOverlay(prefs.chatSide, prefs.chatHotkey) : hideOverlay()).catch(
      () => {},
    )
  }

  async function hostChat(text: string) {
    try {
      await sendChat(text)
    } catch (err) {
      error = errorMessage(err)
    }
  }

  async function refreshWindows() {
    try {
      const [found, processes, displays] = await Promise.all([
        listWindows(),
        listAudioProcesses(),
        listMonitors(),
      ])
      windows = found
      monitors = displays
      if (!displays.some((m) => m.index === selectedMonitor)) selectedMonitor = null
      audioProcesses = processes
      if (!live && selectedHwnd !== null && !found.some((w) => w.hwnd === selectedHwnd)) {
        selectedHwnd = null
      }
    } catch (err) {
      error = errorMessage(err)
    }
  }

  // A window that closed while it was picked cannot be shared.
  $effect(() => {
    if (!pickerActive || selectedHwnd === null || windows.length === 0) return
    if (!windows.some((w) => w.hwnd === selectedHwnd)) selectedHwnd = null
  })

  async function switchTo(target: SwitchTarget) {
    if (switchingSource) return
    switchingSource = true
    error = null
    const switchingLive = live
    try {
      if (target.kind === 'window') {
        await switchSource(target.window.hwnd)
        if (!live || live !== switchingLive) return
        pickedWindow = target.window
        selectedHwnd = target.window.hwnd
        selectedMonitor = null
      } else {
        await switchSource(0, target.monitor.index)
        if (!live || live !== switchingLive) return
        selectedHwnd = null
        selectedMonitor = target.monitor.index
      }
    } catch (err) {
      if (live && live === switchingLive) error = errorMessage(err)
    } finally {
      switchingSource = false
    }
  }

  async function start() {
    if (busy) return
    if (selectedHwnd === null && selectedMonitor === null) {
      error = t('Pick a window to share first.')
      return
    }
    if ((soundMode === 'app' || soundMode === 'exclude') && effectiveAudioPid === 0) {
      error = t('Choose an audio app in stream settings, or choose no sound.')
      showSettings = true
      return
    }
    busy = true
    error = null
    warnings = []
    chat = []
    let attemptedMode: ShareMode | null = null
    try {
      const checked = await checkAvailability()
      if (destroyed) return
      const mode = availableShareMode(share, signedIn, checked)
      if (!mode) {
        error = t(
          'This sharing mode is unavailable. Choose another mode in settings or check again.',
        )
        showSettings = true
        return
      }
      startupShare = mode
      attemptedMode = mode
      startupStage = mode === 'server' ? 'opening_room' : 'preparing_link'
      live = await startStream(
        {
          hwnd: selectedHwnd ?? 0,
          monitor_index: selectedMonitor,
          audio_pid: effectiveAudioPid,
          audio_follow_window: soundMode === 'app' && audioPidOverride === 'same',
          include_process_tree: true,
          exclude_process_tree: soundMode === 'exclude' || soundMode === 'system',
          exclude_exe: excludeExe,
          bitrate_bps: bitrateMbps * 1_000_000,
          fps,
          viewer_cap: viewerCap,
          password,
          display_name: displayName.trim() || 'Host',
          share: mode,
          ...(mode === 'server' ? { access_token: account.session!.access_token } : {}),
          encoder: prefs.encoder,
        },
        (stage) => {
          if (!destroyed) startupStage = stage
        },
      )
      startedAt = Date.now()
      now = startedAt
      inviteFriends(live)
      if (prefs.miniOnLive) void showMini().catch(() => {})
      if (prefs.chatOverlay) void showOverlay(prefs.chatSide, prefs.chatHotkey).catch(() => {})
    } catch (err) {
      error = errorMessage(err)
      // A successful reachability probe cannot guarantee a tunnel handshake or
      // room admission. Remember failures of those steps until the next check;
      // video/encoder errors do not mean the connection mode is unavailable.
      if (attemptedMode && availability && startupStage !== 'starting_video') {
        availability = {
          ...availability,
          [attemptedMode]: { available: false, reason: error },
        }
        showSettings = true
        // The reason now lives with the mode in connection settings, which
        // also offers "Check again". A red banner would read as fatal.
        error = null
      }
    } finally {
      startupStage = null
      busy = false
    }
  }

  async function stop() {
    busy = true
    try {
      await stopStream()
      resetLive()
      void hideMini().catch(() => {})
      void hideOverlay().catch(() => {})
      void stopMarks().catch(() => {})
    } catch (err) {
      error = errorMessage(err)
    } finally {
      busy = false
    }
  }

  async function copyLink() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      copied = true
      setTimeout(() => (copied = false), 1500)
    } catch {
      // Clipboard access can be denied; the link is on screen either way.
    }
  }
</script>

{#snippet availabilityNotice()}
  <div class="stack" style="gap: 8px" aria-live="polite">
    {#if !availability}
      <p class="faint">
        {availabilityError
          ? t('Could not check sharing availability.')
          : t('Checking sharing modes…')}
      </p>
    {:else}
      {#each ['server', 'tunnel', 'lan'] as mode (mode)}
        {@const status = availability[mode as ShareMode]}
        {#if !status.available && status.reason}
          <p class="faint">{t(status.reason)}</p>
        {/if}
      {/each}
      {#if !signedIn && availability.server.available}
        <p class="faint">
          {t('Sign in to use the Sofa server. P2P and Local network work without an account.')}
        </p>
      {/if}
      {#if share === 'auto' && signedIn && !availability.server.available && availability.tunnel.available}
        <p class="faint">
          {t('Automatic will use P2P through Cloudflare while the Sofa server is unavailable.')}
        </p>
      {/if}
    {/if}
    <button
      type="button"
      class="link-btn"
      disabled={checkingAvailability || busy}
      onclick={() => void checkAvailability()}
    >
      {checkingAvailability ? t('Checking…') : t('Check again')}
    </button>
  </div>
{/snippet}

<div class="host-shell">
  {#if inDesktopApp && roomCode && friends.userId && (!live?.share || live.share === 'server')}
    <FriendsRail
      room={{
        viewers: viewerEntries.filter(([, v]) => v.connected).map(([, v]) => v.name),
        invited,
        linkCopied: copied,
        onInvite: (id) => {
          if (!roomCode) return
          friends.invite(id, roomCode, shareUrl)
          invited[id] = true
        },
        onSendLink: () => void copyLink(),
      }}
    />
  {/if}
  <div class="page host stack">
    {#if !inDesktopApp}
      <div>
        <h1>Host a window</h1>
        <p class="muted" style="margin: 0">Pick a window, pick a bitrate, send the link.</p>
      </div>
      <div class="notice notice--warn">
        <strong>{t('To share a window, open the Sofa desktop app.')}</strong>
        <p style="margin: 8px 0 0">
          {t(
            'A browser can watch a room but cannot capture your windows. Watching works here; sharing needs the app on Windows.',
          )}
        </p>
      </div>
      <div class="row">
        {#if dev.on}
          <a href="/debug"><button type="button">{t('Protocol debug')}</button></a>
        {/if}
        <a href="/"><button type="button" class="ghost">{t('Join a room instead')}</button></a>
      </div>
    {:else if restoring}
      <Loading label={t('Finding your seat…')} />
    {:else if restoreError}
      <div class="stack page--narrow" style="margin: auto; text-align: center">
        <Loading label={t('Your seat is taking a little longer.')} waiting={false} />
        <p class="muted">
          {t('Could not reconnect to your stream. Try again to recover the controls.')}
        </p>
        <button type="button" class="primary" onclick={restoreSession}>{t('Try again')}</button>
      </div>
    {:else if !prefs.onboarded && !roomCode}
      <div class="onboard">
        <div class="onboard__pitch">
          <div class="stack" style="gap: 24px">
            <Brand wordmark={false} size={24} />
            <h1>{t('Your window. Your friends. Nobody in between.')}</h1>
            <p>
              {t(
                'Share one window and its sound. Friends open a link in any browser: no app, no account.',
              )}
            </p>
          </div>
          <ul class="onboard__points">
            <li>
              <svg viewBox="0 -960 960 960" aria-hidden="true"
                ><path
                  d="M140-160q-24 0-42-18t-18-42v-520q0-24 18-42t42-18h680q24 0 42 18t18 42v520q0 24-18 42t-42 18H140Zm0-60h680v-436H140v436Z"
                /></svg
              >
              {t('Share a window, or explicitly choose a whole screen')}
            </li>
            <li>
              <svg viewBox="0 -960 960 960" aria-hidden="true"
                ><path
                  d="M220-80q-24.75 0-42.37-17.63Q160-115.25 160-140v-434q0-24.75 17.63-42.38Q195.25-634 220-634h70v-96q0-78.85 55.61-134.42Q401.21-920 480.11-920q78.89 0 134.39 55.58Q670-808.85 670-730v96h70q24.75 0 42.38 17.62Q800-598.75 800-574v434q0 24.75-17.62 42.37Q764.75-80 740-80H220Zm0-60h520v-434H220v434Zm314.5-162.03Q557-324.06 557-355q0-30-22.67-54.5t-54.5-24.5q-31.83 0-54.33 24.5t-22.5 55q0 30.5 22.67 52.5t54.5 22q31.83 0 54.33-22.03ZM350-634h260v-96q0-54.17-37.88-92.08-37.88-37.92-92-37.92T388-822.08q-38 37.91-38 92.08v96ZM220-140v-434 434Z"
                /></svg
              >
              {t('Encrypted, straight from this PC to each friend')}
            </li>
            <li>
              <svg viewBox="0 -960 960 960" aria-hidden="true"
                ><path
                  d="M38-254q0-35 18-63.5t50-42.5q73-32 131.5-46T358-420q62 0 120 14t131 46q32 14 50.5 42.5T678-254v34q0 25-17.5 42.5T618-160H98q-25 0-42.5-17.5T38-220v-34Zm824 94H724q5-15 9.5-29.5T738-220v-34q0-63-29-101.5T622-420q69 8 130 22t99 34q33 19 52 47t19 63v34q0 25-17.5 42.5T862-160ZM250-523q-42-42-42-108t42-108q42-42 108-42t108 42q42 42 42 108t-42 108q-42 42-108 42t-108-42Zm426 0q-42 42-108 42-11 0-24.5-1.5T519-488q24-25 36.5-61.5T568-631q0-45-12.5-79.5T519-774q11-3 24.5-5t24.5-2q66 0 108 42t42 108q0 66-42 108ZM98-220h520v-34q0-16-9.5-31T585-306q-72-32-121-43t-106-11q-57 0-106.5 11T130-306q-14 6-23 21t-9 31v34Zm324.5-346.5Q448-592 448-631t-25.5-64.5Q397-721 358-721t-64.5 25.5Q268-670 268-631t25.5 64.5Q319-541 358-541t64.5-25.5ZM358-220Zm0-411Z"
                /></svg
              >
              {t('Up to 4 friends, 1080p at 60 fps')}
            </li>
          </ul>
        </div>
        <form
          class="onboard__form"
          onsubmit={(event) => {
            event.preventDefault()
            finishOnboarding()
          }}
        >
          <div class="sheet-field">
            <label for="first-name">{t('What should friends call you?')}</label>
            <input
              id="first-name"
              type="text"
              bind:value={draftName}
              placeholder="Host"
              maxlength="32"
            />
          </div>
          <div class="sheet-field">
            <span class="sheet-field__label">{t('Where are your friends?')}</span>
            <button
              type="button"
              class="choice"
              aria-pressed={draftShare === 'auto'}
              disabled={modeUnavailable('auto')}
              onclick={() => (draftShare = 'auto')}
            >
              <span style="font-weight: 600">{t('Automatic')}</span>
              <span
                >{t('Uses the Sofa server when signed in, with Cloudflare P2P as fallback.')}</span
              >
            </button>
            <button
              type="button"
              class="choice"
              aria-pressed={draftShare === 'tunnel'}
              disabled={modeUnavailable('tunnel')}
              onclick={() => (draftShare = 'tunnel')}
            >
              <span style="font-weight: 600">{t('P2P through Cloudflare')}</span>
              <span>{t('Connect directly to viewers using a public link. No ports to open.')}</span>
            </button>
            <button
              type="button"
              class="choice"
              aria-pressed={draftShare === 'lan'}
              disabled={modeUnavailable('lan')}
              onclick={() => (draftShare = 'lan')}
            >
              <span style="font-weight: 600">{t('Local network')}</span>
              <span>{t('Local network only. Nothing leaves your home.')}</span>
            </button>
          </div>
          <div class="spread">
            <span class="faint">{t('You can change both later.')}</span>
            <button type="submit" class="primary" style="height: 52px; padding: 0 28px"
              >{t('Grab a seat →')}</button
            >
          </div>
          {@render availabilityNotice()}
        </form>
      </div>
    {:else}
      {#if roomCode}
        <div class="live-head">
          <span class="live-pill"><span class="dot"></span>LIVE</span>
          <span class="live-head__timer">{elapsed}</span>
          <SwitchWindow
            switching={switchingSource}
            currentHwnd={selectedHwnd}
            currentMonitor={selectedMonitor}
            label={selectedWindow?.title || selectedWindow?.exe || t('Screen')}
            onSwitch={switchTo}
          />
          <span style="flex: 1"></span>
          {#if inDesktopApp}
            <button
              type="button"
              class="ghost"
              aria-pressed={prefs.chatOverlay}
              title={t('Press {key} to type', { key: prefs.chatHotkey })}
              onclick={toggleChatOverlay}
            >
              {t('Chat overlay')}: {prefs.chatOverlay ? t('On') : t('Off')}
            </button>
          {/if}
          <button type="button" class="ghost" onclick={() => toggleMini().catch(() => {})}>
            {t('Mini view')}
          </button>
          {#if dev.on}
            <button type="button" class="ghost" onclick={() => (showAdv = !showAdv)}>
              {showAdv ? t('Hide stats') : t('Stats')}
            </button>
          {/if}
          <button type="button" class="btn-stop" onclick={stop} disabled={busy}>
            {t('Stop sharing')}
          </button>
        </div>

        {#if error}
          <ProblemNotice problem={appProblem(error, 'sharing')} compact />
        {/if}

        <div class="live-layout">
          <div class="live-main">
            <div
              class="preview"
              style="--ar: {live ? `${live.width} / ${live.height}` : '16 / 9'}; --ar-n: {live
                ? live.width / live.height
                : 16 / 9}"
            >
              {#if previewUrl}
                <img
                  class="preview__img"
                  src={previewUrl}
                  alt={t('Live preview of your shared window')}
                />
              {:else}
                <span
                  >{t('live preview · {title}', {
                    title: selectedWindow?.title ?? t('your window'),
                  })}</span
                >
              {/if}
              <div class="preview__status">
                {#if stats?.frame_rate_reduced}
                  <span
                    class="chip chip--warn"
                    title={t(
                      "Other video on this PC is using the GPU's video engine, so the stream runs at an even half rate instead of stuttering.",
                    )}>{t('Smooth {fps} fps: your GPU is busy', { fps: Math.round(fps / 2) })}</span
                  >
                {/if}
                {#if limitedByViewer}
                  <span class="chip chip--warn"
                    >{slowViewer
                      ? t("Softer picture for {name}'s connection", { name: slowViewer.name })
                      : t('Softer picture for a slow connection')}</span
                  >
                {/if}
                {#if !previewUnavailable && !stats?.frame_rate_reduced && !limitedByViewer}
                  <span class="chip"><span class="dot"></span>{t('Looking great')}</span>
                {/if}
              </div>
              {#if dev.on && showAdv && live && stats}
                <div class="stage__stats" style="left: auto; top: auto; right: 14px; bottom: 14px">
                  <div>{live.width}×{live.height} · {stats.fps.toFixed(0)} fps</div>
                  <div>
                    {(stats.video_kbps / 1000).toFixed(1)} of {(stats.target_kbps / 1000).toFixed(
                      1,
                    )} Mbps · encode {stats.worst_encode_ms.toFixed(1)} ms
                  </div>
                </div>
              {/if}
            </div>

            {#if previewUnavailable}
              <ProblemNotice
                compact
                problem={{
                  title: t('Preview unavailable'),
                  message: t(
                    'Sofa could not show the local preview. This does not necessarily mean your friends lost the stream.',
                  ),
                  tone: 'warning',
                  retry: true,
                }}
                onRetry={() => previewAttempt++}
              />
            {/if}

            <div class="invite-bar">
              <div class="invite-bar__url">
                <div class="eyebrow">{t('Send this to your friends')}</div>
                <code
                  >{shareUrl.replace(/^https?:\/\//, '').replace(roomCode, '')}<b>{roomCode}</b
                  ></code
                >
              </div>
              <button type="button" class="primary" onclick={copyLink}>
                {copied ? t('Copied') : t('Copy link')}
              </button>
            </div>

            {#each warnings as warning, index (index)}
              <div class="notice notice--warn">{warning}</div>
            {/each}
          </div>

          <div class="live-side">
            <div>
              <div class="spread" style="margin-bottom: 10px">
                <h2 class="section-title" style="font-size: 1.25rem">{t('On the sofa')}</h2>
                <span class="faint"
                  >{t('{a} of {b}', { a: viewerEntries.length, b: viewerCap })}</span
                >
              </div>
              {#each viewerEntries as [id, viewer] (id)}
                <div class="seat">
                  <span class="avatar avatar--sm" class:avatar--pending={!viewer.connected}>
                    {viewer.name.slice(0, 1).toUpperCase()}
                  </span>
                  <strong style="flex: 1" class:muted={!viewer.connected}>{viewer.name}</strong>
                  {#if viewer.connected}
                    {@const bars = viewerBars[id] ?? 4}
                    <Signal level={bars} label={barsLabel(bars)} />
                  {:else}
                    <span class="faint">{t('joining…')}</span>
                  {/if}
                </div>
              {/each}
              {#if viewerCap > viewerEntries.length}
                <div class="seat seat--open faint">
                  <span class="avatar avatar--sm"></span>{t('{n} open seats', {
                    n: viewerCap - viewerEntries.length,
                  })}
                </div>
              {/if}
            </div>
            <Chat messages={chat} selfId={null} onSend={hostChat} />
          </div>
        </div>
      {:else}
        <div class="picker">
          <div class="spread" style="padding: 10px 0 0">
            <button
              type="button"
              class="link-btn"
              style="color: var(--text-dim)"
              onclick={() => navigate('/')}>← {t('Back')}</button
            >
            <span class="faint" style="font-weight: 700">{t('Step 1 of 2')}</span>
          </div>
          {#if error}
            <ProblemNotice problem={appProblem(error, 'sharing')} compact />
          {/if}
          {#if !error && (availabilityError || (availability && !usableShare))}
            <div class="notice notice--warn">
              {t(
                'This sharing mode is unavailable. Choose another mode in settings or check again.',
              )}
              <button type="button" class="link-btn" onclick={() => (showSettings = true)}>
                {t('Connection settings')}
              </button>
              <button
                type="button"
                class="link-btn"
                disabled={checkingAvailability || busy}
                onclick={() => void checkAvailability()}
              >
                {checkingAvailability ? t('Checking…') : t('Check again')}
              </button>
            </div>
          {/if}

          {#if monitors.length}
            <div class="picker__screen">
              {#if showScreens || selectedMonitor !== null}
                <select
                  id="monitor"
                  aria-label={t('Or share a whole screen')}
                  bind:value={selectedMonitor}
                  onchange={() => {
                    if (selectedMonitor !== null) {
                      selectedHwnd = null
                      if (soundMode === 'app' || soundMode === 'none') soundMode = 'system'
                    }
                  }}
                >
                  <option value={null}>{t('Choose a screen')}</option>
                  {#each monitors as monitor (monitor.index)}
                    <option value={monitor.index}
                      >{monitor.name} · {monitor.width}×{monitor.height}</option
                    >
                  {/each}
                </select>
                {#if selectedMonitor !== null}
                  <p class="muted">
                    {t(
                      'Everything on this screen is shared, including notifications. All system audio is shared; change this in settings.',
                    )}
                  </p>
                {/if}
              {:else}
                <button type="button" class="link-btn" onclick={() => (showScreens = true)}>
                  {t('Share a whole screen instead')} →
                </button>
              {/if}
            </div>
          {/if}

          <WindowGrid
            active={pickerActive}
            {selectedHwnd}
            playing={playingSound}
            bind:windows
            bind:thumbs
            onPick={(w) => {
              pickedWindow = w
              selectedHwnd = w.hwnd
              selectedMonitor = null
              if (soundMode === 'none') soundMode = 'app'
            }}
          >
            {#snippet head()}<h1 class="picker__title">{t('What are we watching?')}</h1>{/snippet}
          </WindowGrid>

          {#if showSettings}
            <button
              type="button"
              class="scrim"
              aria-label={t('Close stream settings')}
              onclick={() => (showSettings = false)}
            ></button>
            <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="sheet-title">
              <div class="sheet__head">
                <h2 id="sheet-title">{t('How it looks')}</h2>
                <button
                  type="button"
                  class="round-button"
                  aria-label={t('Close')}
                  onclick={() => (showSettings = false)}
                >
                  <svg viewBox="0 -960 960 960" aria-hidden="true"
                    ><path
                      d="M480-438 270-228q-9 9-21 9t-21-9q-9-9-9-21t9-21l210-210-210-210q-9-9-9-21t9-21q9-9 21-9t21 9l210 210 210-210q9-9 21-9t21 9q9 9 9 21t-9 21L522-480l210 210q9 9 9 21t-9 21q-9 9-21 9t-21-9L480-438Z"
                    /></svg
                  >
                </button>
              </div>

              <div class="sheet__body">
                <div class="presets">
                  {#each Object.entries(PRESETS) as [key, p] (key)}
                    <button
                      type="button"
                      class="preset"
                      aria-pressed={preset === key}
                      onclick={() => pickPreset(key as keyof typeof PRESETS)}
                    >
                      <span>{t(p.label, undefined, 'preset')}</span>
                      <span>{p.fps} fps · {p.mbps} Mbps</span>
                    </button>
                  {/each}
                </div>

                <div class="sheet-field">
                  <div class="spread">
                    <label for="bitrate">{t('Quality ceiling')}</label>
                    <span class="mono" style="font-size: 0.875rem">{bitrateMbps} Mbps</span>
                  </div>
                  <input
                    id="bitrate"
                    type="range"
                    min={BITRATE_MIN_MBPS}
                    max={BITRATE_MAX_MBPS}
                    step="1"
                    bind:value={bitrateMbps}
                    oninput={customise}
                    onchange={applyBitrate}
                  />
                  <div class="callout">
                    {t('With {n} seats filled you will upload about', { n: viewerCap })}
                    <strong>{bitrateMbps * viewerCap} Mbps</strong>. {t(
                      "If someone's connection is slow, the picture gets softer for everyone.",
                    )}
                  </div>
                </div>

                <div class="spread">
                  <div class="settings-row__text">
                    <span class="sheet-field__label">{t('Seats on the sofa')}</span>
                    <span class="faint">{t('Each friend uses your upload')}</span>
                  </div>
                  <div class="stepper">
                    <button
                      type="button"
                      aria-label={t('Fewer seats')}
                      disabled={viewerCap <= 1}
                      onclick={() => {
                        viewerCap -= 1
                        persistStreamPrefs()
                      }}>−</button
                    >
                    <span>{viewerCap}</span>
                    <button
                      type="button"
                      aria-label={t('More seats')}
                      disabled={viewerCap >= (info?.max_viewer_cap ?? 16)}
                      onclick={() => {
                        viewerCap += 1
                        persistStreamPrefs()
                      }}>+</button
                    >
                  </div>
                </div>

                <div class="sheet-field">
                  <label for="sound-mode">{t('Sound')}</label>
                  <select id="sound-mode" bind:value={soundMode}>
                    <option value="app">{t('Only the selected app and its helpers')}</option>
                    <option value="system">{t('All system audio')}</option>
                    <option value="exclude"
                      >{t('System audio except the selected app and its helpers')}</option
                    >
                    <option value="none">{t('No sound')}</option>
                  </select>
                  <label for="audio">{t('Sound from')}</label>
                  {#if soundMode === 'system'}
                    <label class="check">
                      <input type="checkbox" bind:checked={skipDiscord} />
                      {t('Leave Discord out of the stream')}
                    </label>
                  {/if}
                  <select
                    id="audio"
                    bind:value={audioPidOverride}
                    disabled={soundMode === 'none' || soundMode === 'system'}
                  >
                    <option value="same">
                      {t('Same app as the window')}{selectedWindow
                        ? ` (${selectedWindow.exe})`
                        : ''}
                    </option>
                    {#each audioProcesses as process (process.pid)}
                      <option value={process.pid}>
                        {process.display_name || process.exe}{process.is_active
                          ? ''
                          : ` ${t('(silent)')}`}
                      </option>
                    {/each}
                  </select>
                  {#if soundMode === 'exclude'}
                    <p class="faint">
                      {t(
                        'Select Discord to keep calls out of the stream. All other system audio is shared.',
                      )}
                    </p>
                  {/if}
                </div>

                <div class="sheet-field">
                  <label for="share">{t('Viewers connect through')}</label>
                  <select
                    id="share"
                    bind:value={share}
                    onchange={persistStreamPrefs}
                    disabled={busy}
                  >
                    <option value="auto" disabled={modeUnavailable('auto')}>{t('Automatic')}</option
                    >
                    <option value="server" disabled={modeUnavailable('server')}
                      >{t('Sofa server')}{modeUnavailable('server')
                        ? ` · ${t('Unavailable')}`
                        : ''}</option
                    >
                    <option value="tunnel" disabled={modeUnavailable('tunnel')}
                      >{t('P2P through Cloudflare')}{modeUnavailable('tunnel')
                        ? ` · ${t('Unavailable')}`
                        : ''}</option
                    >
                    <option value="lan" disabled={modeUnavailable('lan')}
                      >{t('Local network')}{modeUnavailable('lan')
                        ? ` · ${t('Unavailable')}`
                        : ''}</option
                    >
                  </select>
                  <p class="field-hint">
                    {share === 'auto'
                      ? t('Uses the Sofa server when signed in, with Cloudflare P2P as fallback.')
                      : share === 'server'
                        ? t('Share through the Sofa server. Requires signing in.')
                        : share === 'tunnel'
                          ? t('Connect directly to viewers using a public link. No ports to open.')
                          : t('Local network only. Nothing leaves your home.')}
                  </p>
                  {@render availabilityNotice()}
                </div>

                <div class="sheet-field">
                  <label for="password"
                    >{t('Room password')} <span class="faint">{t('· optional')}</span></label
                  >
                  <input
                    id="password"
                    type="password"
                    bind:value={password}
                    placeholder={t('Anyone with the link can join')}
                    autocomplete="off"
                    maxlength="64"
                  />
                </div>
              </div>

              <div class="sheet__foot">
                <button type="button" class="ghost" onclick={() => pickPreset('smooth')}
                  >{t('Reset')}</button
                >
                <button type="button" class="danger" onclick={() => (showSettings = false)}
                  >{t('Done')}</button
                >
              </div>
            </div>
          {/if}

          <div class="dock">
            {#if startupStage}
              <StartupProgress stage={startupStage} share={startupShare} />
            {/if}
            {#if selectedHwnd !== null && thumbs[selectedHwnd]}
              <img class="dock__thumb" src={thumbs[selectedHwnd]} alt="" />
            {:else}
              <span class="dock__thumb"></span>
            {/if}
            <div class="dock__what">
              <span
                >{t('Sharing')}{selectedWindow && playingSound(selectedWindow.pid)
                  ? ` · ${t('with sound')}`
                  : ''}</span
              >
              <span>
                {selectedWindow
                  ? selectedWindow.title || selectedWindow.exe
                  : selectedMonitor !== null
                    ? monitors.find((m) => m.index === selectedMonitor)?.name
                    : t('Pick a window above')}
              </span>
            </div>
            {#if tellList.length}
              <div class="dock__tell">
                <span class="eyebrow">{t('Tell')}</span>
                {#each tellList as friend (friend.user_id)}
                  {@const away = friend.presence.state === 'offline'}
                  <button
                    type="button"
                    class="tell-chip"
                    class:tell-chip--on={!away && !tellOff[friend.user_id]}
                    disabled={away}
                    aria-pressed={!away && !tellOff[friend.user_id]}
                    onclick={() => (tellOff[friend.user_id] = !tellOff[friend.user_id])}
                  >
                    <span class="avatar tell-chip__avatar"
                      >{(friend.name[0] ?? '?').toUpperCase()}</span
                    >{friend.name}
                  </button>
                {/each}
              </div>
            {/if}
            <button
              type="button"
              class="dock__chip"
              onclick={() => (showSettings = true)}
              disabled={busy}
            >
              {presetLabel} · {fps} fps · {shareLabel} ▾
            </button>
            <button
              type="button"
              class="primary dock__go"
              onclick={start}
              disabled={busy || !usableShare || (selectedHwnd === null && selectedMonitor === null)}
            >
              <span class="dot"></span>{busy
                ? t('Starting…')
                : !availability
                  ? t('Checking…')
                  : !usableShare
                    ? t('Choose a connection')
                    : t('Go live')}
            </button>
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>
